import * as React from 'react'
import { Icon } from '/react4xp/components/primitives/Icon'

export interface MenuLink {
  label: string
  url: string
  newTab: boolean
}

export interface MenuGroup {
  heading: string
  links: MenuLink[]
}

export interface MenuCta {
  label: string
  url: string
}

export interface MenuData {
  groups: MenuGroup[]
  cta?: MenuCta
  searchPageUrl?: string
}

export interface MainMenuProps {
  menu: MenuData
  onClose: () => void
}

export const MainMenu: React.FC<MainMenuProps> = ({ menu, onClose }) => {
  const navRef = React.useRef<HTMLElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const [isVisible, setIsVisible] = React.useState(false)

  // Trigger enter animation on mount
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  // Escape key closes menu
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Focus trap
  React.useEffect(() => {
    const nav = navRef.current
    if (!nav) return

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusable = nav.querySelectorAll<HTMLElement>(
        'a[href], button, input, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [])

  // Lock body scroll
  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  return (
    <>
      {/* Backdrop — desktop only */}
      <div
        className={`fixed inset-0 z-40 hidden bg-black/30 md:block transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu panel — mobile: full-screen overlay, desktop: container-aligned to match floating nav widget */}
      <div className="fixed inset-0 z-50 pointer-events-none md:inset-auto md:top-0 md:left-0 md:right-0">
        <div className="contents md:mx-auto md:flex md:max-w-container md:justify-end md:px-16 md:py-6">
          <nav
            ref={navRef}
            aria-label="Hovedmeny"
            className={`pointer-events-auto h-full overflow-y-auto bg-white transition-all duration-200 ease-out md:h-auto md:w-3/4 md:max-w-3xl md:max-h-[calc(100vh-6rem)] md:rounded-[27px] md:shadow-lg ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          >
            {/* Top bar */}
            <div className="flex items-center gap-1 p-1.5">
              {/* Search form — native action/method for progressive enhancement */}
              <form
                action={menu.searchPageUrl || '#'}
                method="get"
                className="flex h-10 flex-1 items-center gap-1 rounded-full bg-slate-100 pl-2 pr-4 has-[input:focus-visible]:outline-1 has-[input:focus-visible]:outline-slate-300"
              >
                <button
                  type="submit"
                  className="flex shrink-0 items-center rounded-full text-slate-700 hover:text-black focus-visible:outline-1 focus-visible:outline-slate-300"
                  aria-label="Søk"
                >
                  <Icon name="search" />
                </button>
                <input
                  ref={searchInputRef}
                  type="search"
                  name="q"
                  placeholder="Søk"
                  className="min-w-0 flex-1 bg-transparent text-lg text-black placeholder:text-slate-700 focus:outline-none"
                  aria-label="Søk på nettstedet"
                />
              </form>

              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 shrink-0 cursor-pointer items-center gap-4 px-3 text-xs uppercase tracking-wider text-black focus-visible:outline-1 focus-visible:outline-slate-300"
                aria-label="Lukk meny"
              >
                <span className="hidden md:inline">Meny</span>
                <Icon name="close" />
              </button>
            </div>

            {/* Link groups */}
            <div className="flex flex-col gap-5 px-10 pt-8 md:flex-row md:gap-20 md:pt-16">
              {menu.groups.map((group, i) => (
                <div key={i} className="flex flex-col gap-5 md:gap-6">
                  <h2 className="text-lg font-bold text-blue-900">
                    {group.heading}
                  </h2>
                  <ul className="flex flex-col gap-2 md:gap-3">
                    {group.links.map((link, j) => (
                      <li key={j}>
                        <a
                          href={link.url}
                          className="text-lg text-black underline decoration-blue-500 transition-colors hover:decoration-blue-300 focus-visible:outline-1 focus-visible:outline-slate-300"
                          {...(link.newTab
                            ? { target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                        >
                          {link.label}
                          {link.newTab && (
                            <Icon
                              name="north_east"
                              size={16}
                              className="ml-1 align-middle"
                            />
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Divider + CTA */}
            <div className="px-10 pb-8 pt-6 md:pb-10 md:pt-8">
              <hr className="border-t border-slate-300" />
              {menu.cta && (
                <a
                  href={menu.cta.url}
                  className="mt-6 inline-block rounded-full bg-peach-500 px-6 py-3 text-xs uppercase tracking-wider text-black no-underline transition-colors hover:bg-blue-500 focus-visible:outline-1 focus-visible:outline-slate-300 md:px-8 md:py-4"
                >
                  {menu.cta.label}
                </a>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}
